import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { initials } from "@/lib/format";
import { InterestsPicker } from "@/components/onboarding/InterestsPicker";
import { CardCustomizer } from "@/components/member-card/CardCustomizer";
import { NotificationsSettings } from "@/components/notifications/NotificationsSettings";
import { ChangePasswordCard } from "@/components/account/ChangePasswordCard";
import { Loader2, Upload, ShieldAlert, Trash2, Mail } from "lucide-react";

const PRIVACY_FIELDS = [
  { key: "email", label: "Email address" },
  { key: "phone", label: "Phone number" },
  { key: "title", label: "Job title" },
  { key: "company", label: "Company" },
  { key: "bio", label: "Bio" },
  { key: "avatar", label: "Profile photo" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "website", label: "Website" },
];

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [privacy, setPrivacy] = useState({});
  const [hidden, setHidden] = useState(false);
  const [optout, setOptout] = useState(false);
  const [emailNotif, setEmailNotif] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", title: user.title || "", company: user.company || "",
        phone: user.phone || "", location_text: user.location_text || "", bio: user.bio || "",
        linkedin: user.linkedin || "", website: user.website || "", avatar: user.avatar || "",
        headline: user.headline || "", interests: user.interests || [], seeking: user.seeking || "",
        card_accent: user.card_accent || "copper", card_cover: user.card_cover || "",
      });
      setPrivacy(user.privacy || {});
      setHidden(!!user.hidden_from_directory);
      setOptout(!!user.leaderboard_optout);
      setEmailNotif(!user.email_opt_out);
    }
  }, [user]);

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please choose an image under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "avatars");
      const res = await api.post("/storage/upload", fd);
      setForm((f) => ({ ...f, avatar: res.data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed — please try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await api.put("/users/me", form);
      setUser(res.data);
      toast.success("Profile saved");
    } catch (e) { toast.error("Could not save profile"); } finally { setSaving(false); }
  };

  const savePrivacy = async () => {
    setSaving(true);
    try {
      const res = await api.put("/users/me", { privacy, hidden_from_directory: hidden, leaderboard_optout: optout, email_opt_out: !emailNotif });
      setUser(res.data);
      toast.success("Privacy settings updated");
    } catch (e) { toast.error("Could not save privacy settings"); } finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/users/me");
      await logout();
      toast.success("Your account has been deleted");
      navigate("/");
    } catch (e) { toast.error("Could not delete account"); setDeleting(false); }
  };

  if (!form) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Profile & Privacy</h1>
        <p className="mt-1 text-muted-foreground">Manage how you appear to other members.</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="profile-tab">Profile</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="profile-privacy-tab">Privacy</TabsTrigger>
          <TabsTrigger value="security" data-testid="profile-security-tab">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card className="border-border bg-card"><CardContent className="space-y-5 p-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16"><AvatarImage key={form.avatar || "no-avatar"} src={form.avatar || undefined} data-testid="profile-avatar-image" /><AvatarFallback className="bg-primary text-lg text-primary-foreground">{initials(form.name)}</AvatarFallback></Avatar>
              <div>
                <input id="avatar" type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="profile-avatar-input" />
                <Button asChild variant="secondary" size="sm" disabled={uploading}><label htmlFor="avatar" className="cursor-pointer">{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{uploading ? "Uploading…" : "Upload photo"}</label></Button>
                <p className="mt-1 text-xs text-muted-foreground">PNG or JPG, up to 10MB. Stored on Cloudflare R2.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Full name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" /></div>
              <div><Label>Job title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Company</Label><Input className="mt-1.5" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Location</Label><Input className="mt-1.5" value={form.location_text} onChange={(e) => setForm({ ...form, location_text: e.target.value })} placeholder="City, State" /></div>
              <div><Label>LinkedIn</Label><Input className="mt-1.5" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/..." /></div>
              <div className="sm:col-span-2"><Label>Website</Label><Input className="mt-1.5" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." /></div>
              <div className="sm:col-span-2"><Label>Ask me about <span className="text-muted-foreground">(short headline)</span></Label><Input className="mt-1.5" maxLength={70} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. referrals, web design, scaling a team…" data-testid="profile-headline-input" /></div>
              <div className="sm:col-span-2"><Label>Bio</Label><Textarea className="mt-1.5" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell the network about yourself…" /></div>
            </div>
            <div>
              <Label className="mb-2 block">Interests</Label>
              <InterestsPicker value={form.interests} onChange={(v) => setForm({ ...form, interests: v })} />
            </div>
            <div>
              <Label>What are you looking for?</Label>
              <Textarea className="mt-1.5" rows={2} value={form.seeking} onChange={(e) => setForm({ ...form, seeking: e.target.value })} placeholder="e.g. referral partners, a great accountant, podcast guests…" data-testid="profile-seeking-input" />
            </div>
            <div className="rounded-xl border border-border bg-secondary/40 p-4">
              <p className="text-sm font-medium text-foreground">Card style</p>
              <p className="mb-3 text-xs text-muted-foreground">Personalize the look of your member card and public profile.</p>
              <CardCustomizer
                accent={form.card_accent}
                cover={form.card_cover}
                onAccentChange={(k) => setForm({ ...form, card_accent: k })}
                onCoverChange={(url) => setForm({ ...form, card_cover: url || "" })}
              />
            </div>
            <Button onClick={saveProfile} disabled={saving || uploading} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="profile-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="privacy" className="mt-6">
          <Card className="border-border bg-card"><CardContent className="p-6">
            <NotificationsSettings />
            <div className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/60 p-4">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-medium text-foreground"><Mail className="h-4 w-4 text-primary" /> Email notifications</p>
                <p className="mt-0.5 text-sm text-muted-foreground">Occasional event reminders and group announcements by email. Security emails (like a password reset you request) are always sent.</p>
              </div>
              <Switch checked={emailNotif} onCheckedChange={setEmailNotif} data-testid="privacy-email-toggle" />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl border border-border bg-secondary/60 p-4">
              <div><p className="font-medium text-foreground">Member networking &amp; directory</p><p className="text-sm text-muted-foreground">When off, you're hidden from the member directory and networking — other members won't see your profile at all.</p></div>
              <Switch checked={!hidden} onCheckedChange={(v) => setHidden(!v)} data-testid="privacy-hidden-toggle" />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl border border-border bg-secondary/60 p-4">
              <div><p className="font-medium text-foreground">Hide me from the leaderboard</p><p className="text-sm text-muted-foreground">When on, you still earn points but won't appear in the public Rewards leaderboard.</p></div>
              <Switch checked={optout} onCheckedChange={setOptout} data-testid="privacy-leaderboard-toggle" />
            </div>
            <div className="my-4 h-px bg-border" />
            <p className="text-sm font-medium text-foreground">Choose what other members can see:</p>
            <div className="mt-3 divide-y divide-border">
              {PRIVACY_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center justify-between py-3">
                  <span className="text-sm text-foreground">{f.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{privacy[f.key] ? "Hidden" : "Visible"}</span>
                    <Switch checked={!privacy[f.key]} onCheckedChange={(v) => setPrivacy({ ...privacy, [f.key]: !v })} data-testid={`privacy-${f.key}-toggle`} />
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={savePrivacy} disabled={saving} className="mt-5 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="privacy-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save privacy settings"}</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6 space-y-6">
          <ChangePasswordCard />
          <Card className="border-destructive/30 bg-card"><CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10"><ShieldAlert className="h-5 w-5 text-destructive" /></div>
              <div className="flex-1">
                <h3 className="font-serif text-lg font-semibold text-foreground">Delete account</h3>
                <p className="mt-1 text-sm text-muted-foreground">This permanently removes your account and business profile. This cannot be undone.</p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="mt-4" data-testid="account-delete-button"><Trash2 className="mr-2 h-4 w-4" />Delete my account</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="account-delete-confirm-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                      <AlertDialogDescription>All your personal data will be permanently erased. This action is irreversible.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteAccount} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="account-delete-confirm">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, delete everything"}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
